import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useGetRemitAccuracyClusterQuery } from "store/queries/remitAccuracy";
import {
  IRemitCluster,
  IRemitClusterRequest,
  RemitScoredBenefit,
} from "types/RemitAccuracy/RemitAccuracy";

import { RemitAccuracyExampleResponse } from "./RemitAccuracyExampleResponse";
import {
  PverifyRequestLink,
  RemitClaimLink,
  RemitClaimModal,
} from "./RemitAccuracyLinks";
import {
  AI_AGREEMENT_LABELS,
  formatConfidence,
  formatRemitValue,
  READ_FROM_LABELS,
  REMIT_BENEFIT_LABELS,
  REMIT_KIND_LABELS,
  REMIT_OUTCOME_LABELS,
} from "./remitAccuracyWording";

type IClusterRow = IRemitCluster["rows"][number];

const PAGE_SIZE = 100;

/** Only the first checks' stored responses are read; the rest say so rather than showing a blank. */
const remitLineBody = (row: IClusterRow) => {
  if (!row.examined)
    return <span className="text-color-secondary">not examined</span>;
  return row.remitLine ?? "—";
};

const outcomeBody = (row: IClusterRow) => (
  <span className="flex align-items-center gap-2 white-space-nowrap">
    <Tag
      severity={row.outcome === "MATCH" ? "success" : "danger"}
      value={REMIT_OUTCOME_LABELS[row.outcome]}
    />
    {row.outcome === "ENGINE_NO_VALUE" && (
      <span className="text-color-secondary">no value</span>
    )}
  </span>
);

const requestBody = (row: IClusterRow) => (
  <PverifyRequestLink requestId={row.pverifyRequestId} />
);

/**
 * CLE-2818 — one miss cluster: the checks in it, and what an example stored response looks like on the
 * service line the engine read. Read-only.
 */
export function RemitAccuracyCluster({
  request,
  onBack,
}: {
  request: IRemitClusterRequest;
  onBack: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isFetching, isError } =
    useGetRemitAccuracyClusterQuery({ ...request, limit: PAGE_SIZE, offset });
  const [openClaim, setOpenClaim] = useState<string | null>(null);
  const cluster = data?.data;
  const benefit = request.benefit as RemitScoredBenefit;

  const value = (amount: number | null) => formatRemitValue(amount, benefit);
  /** An engine or remit cell: "no value" in the muted colour rather than a dash. */
  const valueCell = (amount: number | null) =>
    amount === null ? (
      <span className="text-color-secondary">no value</span>
    ) : (
      value(amount)
    );

  // CLE-2820 — the AI's side appears only when the cluster was asked for with the comparison.
  const includeAi = Boolean(request.includeAi);
  const aiBody = (row: IClusterRow) =>
    row.aiOutcome === null || row.aiOutcome === undefined ? (
      <span className="text-color-secondary">not asked</span>
    ) : (
      <span className="flex flex-column gap-1 white-space-nowrap">
        <span>
          {valueCell(row.aiValue ?? null)} ·{" "}
          {formatConfidence(row.aiConfidence)}
        </span>
        {row.aiAgreement && (
          <span className="text-xs text-color-secondary">
            {AI_AGREEMENT_LABELS[row.aiAgreement]}
          </span>
        )}
      </span>
    );

  const linksBody = (row: IClusterRow) => (
    <div className="flex gap-2 align-items-center white-space-nowrap">
      {row.orderIds[0] && (
        <Link
          to={`/orders/${row.orderIds[0]}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Estimate
        </Link>
      )}
    </div>
  );

  const remitBody = (row: IClusterRow) => (
    <RemitClaimLink
      claimId={row.claimIds[0]}
      text={
        row.remitValue === null
          ? row.remitValues.map(value).join(", ")
          : value(row.remitValue)
      }
      onOpen={setOpenClaim}
    />
  );

  if (isError) {
    return <Message severity="error" text="The cluster could not be loaded." />;
  }

  return (
    <div data-testid="remitAccuracy.cluster">
      <Button
        type="button"
        icon="pi pi-angle-left"
        label="Remit accuracy"
        className="p-button-text p-button-sm px-0"
        onClick={onBack}
      />
      <h3 className="mt-3 mb-3">
        {cluster
          ? `${cluster.cluster.accountName} · ${cluster.cluster.payerName} · ${
              cluster.cluster.planName ?? "No plan name"
            } · ${REMIT_BENEFIT_LABELS[benefit]}`
          : "Loading…"}
      </h3>
      {cluster && (
        <div className="flex flex-wrap gap-2 align-items-center mb-3">
          <Tag severity="danger" value={`${cluster.cluster.misses} misses`} />
          <Tag
            severity="success"
            value={`${cluster.cluster.matches} matches`}
          />
          <span className="text-color-secondary">
            {cluster.cluster.checks} remit-confirmed checks · engine read from{" "}
            {READ_FROM_LABELS[cluster.cluster.readFrom] ??
              cluster.cluster.readFrom}{" "}
            · {REMIT_KIND_LABELS[cluster.cluster.kind]}
          </span>
          {includeAi && cluster.cluster.ai && (
            <span
              className="text-color-secondary"
              data-testid="remitAccuracy.cluster.aiAgreement"
            >
              AI vs engine: {cluster.cluster.ai.onlyAi} only AI right ·{" "}
              {cluster.cluster.ai.onlyEngine} only engine right ·{" "}
              {cluster.cluster.ai.bothRight} both right ·{" "}
              {cluster.cluster.ai.bothWrong} both wrong
            </span>
          )}
        </div>
      )}

      <div className="grid mb-3">
        <div className="col-12 lg:col-7">
          <div className="border-1 surface-border border-round p-3 h-full">
            <h4 className="mt-0 mb-3">What the response looks like</h4>
            {cluster?.example ? (
              <RemitAccuracyExampleResponse
                example={cluster.example}
                pattern={cluster.remitLinePattern}
                benefit={benefit}
              />
            ) : (
              !isLoading && <p className="m-0">No example response.</p>
            )}
          </div>
        </div>
        <div className="col-12 lg:col-5">
          {/* Static placeholder — rule recommendations are a later ticket. */}
          <div
            className="border-1 border-dashed surface-border border-round p-3 h-full flex flex-column gap-2 justify-content-center"
            data-testid="remitAccuracy.cluster.recommendationsPlaceholder"
          >
            <Tag
              severity="info"
              value="Coming soon"
              className="align-self-start"
            />
            <h4 className="m-0">Rule recommendations</h4>
            <p className="m-0 text-color-secondary">
              A suggested configuration change for this cluster will appear
              here, with the checks it would fix and break before anyone applies
              it.
            </p>
          </div>
        </div>
      </div>

      <h4 className="mt-5 mb-3">Checks in this cluster · misses first</h4>
      <DataTable
        value={cluster?.rows ?? []}
        loading={isFetching}
        dataKey="pverifyResponseId"
        lazy
        paginator
        rows={PAGE_SIZE}
        first={offset}
        totalRecords={cluster?.total ?? 0}
        onPage={(event) => setOffset(event.first)}
        emptyMessage="No checks."
        data-testid="remitAccuracy.cluster.checks"
      >
        <Column header="Outcome" body={outcomeBody} />
        <Column header="Plan" field="planName" />
        <Column header="Pverify Request" body={requestBody} />
        <Column
          header="Engine"
          body={(row: IClusterRow) => valueCell(row.engineValue)}
        />
        {includeAi && <Column header="AI" body={aiBody} />}
        <Column header="Remit" body={remitBody} />
        <Column header="Line equal to the remit" body={remitLineBody} />
        <Column header="" body={linksBody} />
      </DataTable>

      {openClaim && (
        <RemitClaimModal
          claimId={openClaim}
          accountId={request.accountId}
          onHide={() => setOpenClaim(null)}
        />
      )}
    </div>
  );
}
